/**
 * RTWO Indexer Engine — Domain Models
 * Phase 12 | Module ID: RTWO-IDXR-001
 * Version: 1.0.0-phase12
 *
 * Strongly typed domain models for the RTWO Indexer Engine.
 * Defines the blockchain data indexing layer between the RPC Platform,
 * Node Infrastructure, Ledger Engine, and future Explorer Platform.
 *
 * Truthful Data Policy:
 * - No fake indexed blocks, transactions, addresses, balances, assets, or events.
 * - No fake search results or indexing progress.
 * - Indexing is NOT operational — architecture phase only.
 * - All runtime values are null until real RPC source and index storage are connected.
 *
 * Source: Indexer Engine (RTWO-IDXR-001)
 */

// ─────────────────────────────────────────────
// PRIMITIVE ID TYPES
// ─────────────────────────────────────────────

export type IndexJobId = string;
export type IndexedBlockId = string;
export type IndexedTransactionId = string;
export type IndexedAddressId = string;
export type IndexedAssetId = string;
export type IndexedEventId = string;
export type IndexQueryId = string;
export type StorageBindingId = string;
export type IndexerTimestamp = number | null;

export const INDEXER_ENGINE_VERSION = {
  moduleId: 'RTWO-IDXR-001',
  moduleName: 'Indexer Engine',
  version: '1.0.0-phase12',
  phase: 'Phase 12 — Indexer Engine',
  status: 'Structure Complete — Waiting for Real Blockchain Data',
  productionReadiness: 'Pending RPC Source and Index Storage',
  policy:
    'Truthful Data Policy Enforced — No Fake Indexed Blocks, Transactions, Addresses, Balances, Assets, Events, or Search Results',
} as const;

// ─────────────────────────────────────────────
// INDEX JOB TYPES
// ─────────────────────────────────────────────

export type RTWOIndexJobType =
  | 'block_indexing'
  | 'transaction_indexing'
  | 'address_indexing'
  | 'asset_indexing'
  | 'event_indexing'
  | 'full_sync'
  | 'incremental_sync'
  | 'reindex';

export type RTWOIndexSourceType =
  | 'rpc_platform'
  | 'ledger_engine'
  | 'node_infrastructure'
  | 'consensus_engine'
  | 'blockchain_protocol_engine'
  | 'internal';

// ─────────────────────────────────────────────
// INDEX JOB LIFECYCLE
// ─────────────────────────────────────────────

export type RTWOIndexJobStatus =
  | 'created'
  | 'queued'
  | 'reading_source'
  | 'transforming'
  | 'validating'
  | 'writing_index'
  | 'indexed'
  | 'failed';

export interface IndexJobLifecycleStage {
  status: RTWOIndexJobStatus;
  label: string;
  description: string;
  terminal: boolean;
  requiresRealData: boolean;
  truthfulNote: string;
}

export const INDEX_JOB_LIFECYCLE: IndexJobLifecycleStage[] = [
  {
    status: 'created',
    label: 'Created',
    description:
      'Index job created and registered in the job registry. Job ID assigned. Source reference recorded.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real RPC source to be connected. No fake jobs created.',
  },
  {
    status: 'queued',
    label: 'Queued',
    description: 'Index job queued for processing. Waiting for indexer worker to pick up the job.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real indexer worker infrastructure. No fake queue depth shown.',
  },
  {
    status: 'reading_source',
    label: 'Reading Source',
    description:
      'Indexer is reading raw blockchain data from the configured source (RPC Platform, Ledger Engine, or Node Infrastructure).',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real RPC source connection. No fake blockchain data read.',
  },
  {
    status: 'transforming',
    label: 'Transforming',
    description: 'Raw blockchain data is being transformed into the RTWO index schema format.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real blockchain data. No fake transformation results.',
  },
  {
    status: 'validating',
    label: 'Validating',
    description:
      'Transformed index data is being validated against the RTWO index schema and consistency rules.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real data to validate. No fake validation results.',
  },
  {
    status: 'writing_index',
    label: 'Writing Index',
    description: 'Validated index data is being written to the configured index storage backend.',
    terminal: false,
    requiresRealData: true,
    truthfulNote: 'Requires real index storage to be configured. No fake write operations.',
  },
  {
    status: 'indexed',
    label: 'Indexed',
    description:
      'Index job completed successfully. Data is available in the index storage for query and search.',
    terminal: true,
    requiresRealData: true,
    truthfulNote: 'Terminal success state. Requires real data pipeline end-to-end.',
  },
  {
    status: 'failed',
    label: 'Failed',
    description:
      'Index job failed at any lifecycle stage. Error details recorded. Job may be retried.',
    terminal: true,
    requiresRealData: true,
    truthfulNote: 'Terminal failure state. Error details must come from real backend.',
  },
];

export const INDEX_JOB_STATUS_LABELS: Record<RTWOIndexJobStatus, string> = {
  created: 'Created',
  queued: 'Queued',
  reading_source: 'Reading Source',
  transforming: 'Transforming',
  validating: 'Validating',
  writing_index: 'Writing Index',
  indexed: 'Indexed',
  failed: 'Failed',
};

// ─────────────────────────────────────────────
// INDEXING STATUS (shared across indexed entities)
// ─────────────────────────────────────────────

export type RTWOIndexingStatus =
  | 'architecture_ready'
  | 'waiting_for_rpc_source'
  | 'waiting_for_index_storage'
  | 'not_indexed'
  | 'indexing'
  | 'indexed'
  | 'reindexing'
  | 'failed';

export const INDEXING_STATUS_LABELS: Record<RTWOIndexingStatus, string> = {
  architecture_ready: 'Architecture Ready',
  waiting_for_rpc_source: 'Waiting for RPC Source',
  waiting_for_index_storage: 'Waiting for Index Storage',
  not_indexed: 'Not Indexed',
  indexing: 'Indexing',
  indexed: 'Indexed',
  reindexing: 'Reindexing',
  failed: 'Failed',
};

// ─────────────────────────────────────────────
// DOMAIN MODEL 1: RTWOIndexJob
// ─────────────────────────────────────────────

export interface RTWOIndexJob {
  /** Unique identifier for this index job */
  jobId: IndexJobId;
  /** Type of indexing operation */
  jobType: RTWOIndexJobType;
  /** Source system providing the data */
  sourceType: RTWOIndexSourceType;
  /** Reference to the source data (block number, tx hash, address, etc.) */
  sourceReference: string | null;
  /** Current lifecycle status of the job */
  status: RTWOIndexJobStatus;
  /** Timestamp when the job was created */
  createdAt: IndexerTimestamp;
  /** Timestamp when the job was last updated */
  updatedAt: IndexerTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 2: RTWOIndexedBlock
// ─────────────────────────────────────────────

export interface RTWOIndexedBlock {
  /** Unique identifier for this indexed block record */
  indexedBlockId: IndexedBlockId;
  /** Block number in the RTWO blockchain */
  blockNumber: number | null;
  /** Block hash — reference only, not generated */
  blockHash: string | null;
  /** Parent block hash — reference only */
  parentHash: string | null;
  /** Number of transactions in this block — reference only */
  transactionCount: number | null;
  /** Current indexing status */
  indexingStatus: RTWOIndexingStatus;
  /** Reference to the source that provided this block data */
  sourceReference: string | null;
  /** Timestamp when this block was indexed */
  indexedAt: IndexerTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 3: RTWOIndexedTransaction
// ─────────────────────────────────────────────

export interface RTWOIndexedTransaction {
  /** Unique identifier for this indexed transaction record */
  indexedTransactionId: IndexedTransactionId;
  /** Transaction hash — reference only, not generated */
  transactionHash: string | null;
  /** Block number containing this transaction */
  blockNumber: number | null;
  /** Sender address — reference only, not generated */
  fromAddress: string | null;
  /** Recipient address — reference only, not generated */
  toAddress: string | null;
  /** Asset reference for the transferred asset */
  assetReference: string | null;
  /** Amount reference — not a real balance, reference only */
  amountReference: string | null;
  /** Current indexing status */
  indexingStatus: RTWOIndexingStatus;
  /** Reference to the source that provided this transaction data */
  sourceReference: string | null;
  /** Timestamp when this transaction was indexed */
  indexedAt: IndexerTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 4: RTWOIndexedAddress
// ─────────────────────────────────────────────

export type RTWOAddressType = 'wallet' | 'validator' | 'contract' | 'system' | 'unknown';

export interface RTWOIndexedAddress {
  /** Unique identifier for this indexed address record */
  indexedAddressId: IndexedAddressId;
  /** The blockchain address — reference only, not generated */
  address: string | null;
  /** Type classification of this address */
  addressType: RTWOAddressType | null;
  /** Transaction count reference — not a real count, reference only */
  transactionCountReference: string | null;
  /** Balance reference — not a real balance, reference only */
  balanceReference: string | null;
  /** Current indexing status */
  indexingStatus: RTWOIndexingStatus;
  /** Reference to the source that provided this address data */
  sourceReference: string | null;
  /** Timestamp when this address was indexed */
  indexedAt: IndexerTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 5: RTWOIndexedAsset
// ─────────────────────────────────────────────

export type RTWOIndexedAssetType =
  'native' | 'token' | 'nft' | 'governance_token' | 'staking_token' | 'unknown';

export interface RTWOIndexedAsset {
  /** Unique identifier for this indexed asset record */
  indexedAssetId: IndexedAssetId;
  /** Asset identifier — reference only */
  assetId: string | null;
  /** Asset symbol — reference only, not generated */
  symbol: string | null;
  /** Asset type classification */
  assetType: RTWOIndexedAssetType | null;
  /** Holder count reference — not a real count, reference only */
  holderCountReference: string | null;
  /** Supply reference — not a real supply, reference only */
  supplyReference: string | null;
  /** Current indexing status */
  indexingStatus: RTWOIndexingStatus;
  /** Reference to the source that provided this asset data */
  sourceReference: string | null;
  /** Timestamp when this asset was indexed */
  indexedAt: IndexerTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 6: RTWOIndexedEvent
// ─────────────────────────────────────────────

export type RTWOIndexedEventType =
  | 'block_finalized'
  | 'transaction_confirmed'
  | 'validator_slashed'
  | 'stake_changed'
  | 'governance_vote'
  | 'asset_transfer'
  | 'contract_event'
  | 'system_event'
  | 'unknown';

export interface RTWOIndexedEvent {
  /** Unique identifier for this indexed event record */
  indexedEventId: IndexedEventId;
  /** Type of blockchain event */
  eventType: RTWOIndexedEventType;
  /** Block number where this event occurred */
  blockNumber: number | null;
  /** Transaction hash associated with this event */
  transactionHash: string | null;
  /** Hash of the event payload — reference only */
  payloadHash: string | null;
  /** Current indexing status */
  indexingStatus: RTWOIndexingStatus;
  /** Reference to the source that provided this event data */
  sourceReference: string | null;
  /** Timestamp when this event was indexed */
  indexedAt: IndexerTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 7: RTWOIndexQuery
// ─────────────────────────────────────────────

export type RTWOIndexQueryType =
  | 'block_lookup'
  | 'transaction_lookup'
  | 'address_lookup'
  | 'asset_lookup'
  | 'event_lookup'
  | 'full_text_search'
  | 'range_query'
  | 'filter_query';

export type RTWOIndexQueryStatus =
  'created' | 'executing' | 'completed' | 'no_results' | 'failed' | 'not_available';

export interface RTWOIndexQueryFilter {
  field: string;
  operator: 'eq' | 'gt' | 'lt' | 'gte' | 'lte' | 'contains' | 'in';
  value: string | number | null;
}

export interface RTWOIndexQuery {
  /** Unique identifier for this query */
  queryId: IndexQueryId;
  /** Type of query operation */
  queryType: RTWOIndexQueryType;
  /** Raw query text or search term */
  queryText: string | null;
  /** Optional filters applied to the query */
  filters: RTWOIndexQueryFilter[];
  /** Current query status */
  status: RTWOIndexQueryStatus;
  /** Timestamp when the query was created */
  createdAt: IndexerTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 8: RTWOIndexStorageBinding
// ─────────────────────────────────────────────

export type RTWOIndexType =
  | 'block_index'
  | 'transaction_index'
  | 'address_index'
  | 'asset_index'
  | 'event_index'
  | 'search_index'
  | 'full_index';

export type RTWOIndexStorageProvider =
  'elasticsearch' | 'postgresql' | 'redis' | 'custom_rtwo_store' | 'not_configured';

export type RTWOStorageBindingStatus =
  | 'architecture_ready'
  | 'not_configured'
  | 'not_connected'
  | 'waiting_for_provider'
  | 'connected'
  | 'degraded'
  | 'offline';

export interface RTWOIndexStorageBinding {
  /** Unique identifier for this storage binding */
  bindingId: StorageBindingId;
  /** Type of index this binding serves */
  indexType: RTWOIndexType;
  /** Storage provider for this index */
  storageProvider: RTWOIndexStorageProvider;
  /** Current binding status */
  status: RTWOStorageBindingStatus;
  /** Timestamp when this binding was created */
  createdAt: IndexerTimestamp;
}

// ─────────────────────────────────────────────
// INDEXER ENGINE STATUS
// ─────────────────────────────────────────────

export type RTWOIndexerEngineStatus =
  | 'not_initialized'
  | 'initializing'
  | 'structure_ready'
  | 'waiting_for_rpc_source'
  | 'waiting_for_index_storage'
  | 'indexing'
  | 'error'
  | 'shutdown';

// ─────────────────────────────────────────────
// INDEXER EVENT
// ─────────────────────────────────────────────

export type IndexerEventType =
  | 'job_created'
  | 'job_queued'
  | 'job_started'
  | 'job_completed'
  | 'job_failed'
  | 'block_indexed'
  | 'transaction_indexed'
  | 'address_indexed'
  | 'asset_indexed'
  | 'event_indexed'
  | 'storage_binding_changed'
  | 'rpc_source_connected'
  | 'rpc_source_disconnected';

export interface IndexerEvent {
  eventId: string;
  eventType: IndexerEventType;
  moduleId: 'RTWO-IDXR-001';
  timestamp: IndexerTimestamp;
  payload: Record<string, unknown>;
}

// ─────────────────────────────────────────────
// INDEX TYPE DESCRIPTORS
// ─────────────────────────────────────────────

export interface IndexTypeDescriptor {
  indexType: RTWOIndexType;
  label: string;
  description: string;
  sourceEngine: string;
  storageProvider: RTWOIndexStorageProvider;
  productionReady: boolean;
  emptyState: string;
}

export const INDEX_TYPE_REGISTRY: IndexTypeDescriptor[] = [
  {
    indexType: 'block_index',
    label: 'Block Index',
    description:
      'Indexes all finalized blocks from the RTWO blockchain. Block number, hash, parent hash, and transaction count are indexed for fast lookup.',
    sourceEngine: 'RPC Platform / Ledger Engine',
    storageProvider: 'not_configured',
    productionReady: false,
    emptyState: 'No Indexed Blocks — Waiting for Real Blockchain Data',
  },
  {
    indexType: 'transaction_index',
    label: 'Transaction Index',
    description:
      'Indexes all confirmed transactions. From/to addresses, asset references, and amount references are indexed for Explorer queries.',
    sourceEngine: 'RPC Platform / Ledger Engine',
    storageProvider: 'not_configured',
    productionReady: false,
    emptyState: 'No Indexed Transactions — Waiting for Real Blockchain Data',
  },
  {
    indexType: 'address_index',
    label: 'Address Index',
    description:
      'Indexes all active blockchain addresses. Address type, transaction count reference, and balance reference are indexed.',
    sourceEngine: 'RPC Platform / Ledger Engine',
    storageProvider: 'not_configured',
    productionReady: false,
    emptyState: 'No Indexed Addresses — Waiting for Real Blockchain Data',
  },
  {
    indexType: 'asset_index',
    label: 'Asset Index',
    description:
      'Indexes all registered assets on the RTWO blockchain. Symbol, type, holder count reference, and supply reference are indexed.',
    sourceEngine: 'RPC Platform / Ledger Engine',
    storageProvider: 'not_configured',
    productionReady: false,
    emptyState: 'No Indexed Assets — Waiting for Real Blockchain Data',
  },
  {
    indexType: 'event_index',
    label: 'Event Index',
    description:
      'Indexes all blockchain events including block finalization, transaction confirmation, validator slashing, and governance votes.',
    sourceEngine: 'RPC Platform / Consensus Engine',
    storageProvider: 'not_configured',
    productionReady: false,
    emptyState: 'No Indexed Events — Waiting for Real Blockchain Data',
  },
  {
    indexType: 'search_index',
    label: 'Search Index',
    description:
      'Full-text search index for Explorer queries. Supports block hash, transaction hash, address, and asset symbol search.',
    sourceEngine: 'Indexer Engine (aggregated)',
    storageProvider: 'not_configured',
    productionReady: false,
    emptyState: 'No Search Results — Index Storage Not Configured',
  },
  {
    indexType: 'full_index',
    label: 'Full Index',
    description:
      'Complete blockchain index combining all index types. Used for comprehensive Explorer queries and analytics.',
    sourceEngine: 'Indexer Engine (aggregated)',
    storageProvider: 'not_configured',
    productionReady: false,
    emptyState: 'Full Index Not Available — Waiting for Real Blockchain Data',
  },
];

// ─────────────────────────────────────────────
// SERVICE IDs
// ─────────────────────────────────────────────

export const INDEXER_SERVICE_IDS = {
  JOB_REGISTRY: 'indexer-job-registry',
  BLOCK_INDEXING: 'indexer-block-indexing',
  TRANSACTION_INDEXING: 'indexer-transaction-indexing',
  ADDRESS_INDEXING: 'indexer-address-indexing',
  ASSET_INDEXING: 'indexer-asset-indexing',
  EVENT_INDEXING: 'indexer-event-indexing',
  QUERY_SERVICE: 'indexer-query-service',
  SEARCH_SERVICE: 'indexer-search-service',
  STORAGE_BINDING: 'indexer-storage-binding',
  VALIDATION: 'indexer-validation',
  EVENT_PUBLISHER: 'indexer-event-publisher',
  READINESS: 'indexer-readiness',
} as const;
